import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
    ApplicationModalPropsType,
    BotMessage,
    UserMessage,
} from './jsxElements';
import { Activity } from './types';
import styles from './ReactTranscript.module.css';
import { getTenantName, isMobile, isMobileBrowser, isUnified } from './helpers';
import { AnchoredElementContainer } from './attachments';
import {
    DisableReasons,
    InputMode,
    useDisableTextSend,
    useInputMode,
} from './hooks';
import { LiveChatAgentMessage } from './jsxElements/LiveChatAgentMessage';
import { LiveChatUserMessage } from './jsxElements/LiveChatUserMessage';
import { BotTakingAwhile } from './jsxElements/UserWaitingOnBot/BotTakingAwhile';
import { isUserWaitingEvent } from './utils/conversationStatus';
import UserWaitingOnBot from './jsxElements/UserWaitingOnBot/UserWaitingOnBot';
import { LiveChatService, OnLiveChatClosedInTabEvent } from './liveChatService';
import { LiveChatClosedInTab } from './jsxElements/LiveChatClosedInTab';
import { AmpFlagSet } from './amplitude';

import { MessageRenderer } from './interfaces/messageRenderer';
import { Spinner } from './jsxElements/Spinner';
import { GenericToast } from './attachments/GenericToast';
import { FEEDBACK_TOAST_TIMEOUT_MS } from './constants';
import { FeedbackSelectedStatus } from './attachments/FeedbackQnA';
import { useConversationStatus } from './contextProviders/ConversationStatusContext';
import { InformationalAlert } from './jsxElements/InformationalAlert';
import generalConfig from './resources/general.json';

type ReactTranscriptArgs = {
    messages: Activity[];
    appendMessage?: (message: Activity) => void;
    sendMessage: (message: string) => void | undefined;
    sendPostBack: (message: string) => void | undefined;
    interactedWithBot: boolean;
    sendEvent: (name: string, value: any) => void | undefined;
    featureFlags: AmpFlagSet;
    modalProperties: ApplicationModalPropsType | undefined;
    setInputMode: (inputMode: InputMode) => void;
    setModalProperties:
        | ((modalProperties: ApplicationModalPropsType) => void)
        | undefined;
    liveChatService: LiveChatService | undefined;
    messageRenderer: MessageRenderer;
};

const isUserMessage = (activity: Activity): boolean => {
    return activity.type === 'message' && activity.from.role === 'user';
};

const isBotMessage = (activity: Activity): boolean => {
    return activity.type === 'message' && activity.from.role === 'bot';
};

const isLiveChatUserEvent = (activity: Activity): boolean => {
    return isLiveChatEvent(activity) && activity.value?.from === 'user';
};

const isLiveChatAgentEvent = (activity: Activity): boolean => {
    return isLiveChatEvent(activity) && activity.value?.from === 'agent';
};

const isLiveChatEvent = (activity: Activity): boolean => {
    return activity.type === 'event' && activity.name === 'liveChatMessage';
};

const isMessageWithAttachments = (activity: Activity): boolean => {
    return (
        activity !== undefined &&
        activity.from.role === 'bot' &&
        activity.type === 'message' &&
        activity.attachments !== undefined
    );
};

const ignoreLatestActivity = (latestActivity: Activity): boolean => {
    return (
        latestActivity.name === 'feedbackEvent' ||
        (latestActivity.text !== undefined &&
            latestActivity.text?.startsWith('requestSkill::cancelAppointment'))
    );
};

const getActivityWithAnchoredElements = (
    messages: Activity[]
): Activity | null => {
    if (messages.length === 1) {
        const latestActivity = messages[messages.length - 1];
        return isMessageWithAttachments(latestActivity) ? latestActivity : null;
    } else if (messages.length >= 2) {
        const latestActivity = messages[messages.length - 1];
        const prevActivity = messages[messages.length - 2];
        if (
            isMessageWithAttachments(latestActivity) &&
            !ignoreLatestActivity(latestActivity)
        ) {
            return latestActivity;
        } else if (
            isMessageWithAttachments(prevActivity) &&
            ignoreLatestActivity(latestActivity)
        ) {
            return prevActivity;
        }
        return null;
    }
    return null;
};

// TODO: Remove the hack when experiment is done.
const removeOldLiveChatAttachments = (messages: Activity[]) => {
    for (let i = 0; i < messages.length - 2; ++i) {
        if (messages[i].name === 'liveChatWithAgentLayout') {
            messages[i].attachments = undefined;
        }
    }
};

export const ReactTranscript: React.FC<ReactTranscriptArgs> = ({
    messages,
    appendMessage,
    sendMessage,
    sendPostBack,
    interactedWithBot,
    sendEvent,
    featureFlags,
    setInputMode,
    setModalProperties,
    liveChatService,
    messageRenderer,
}: ReactTranscriptArgs) => {
    // Scrolling doc: https://github.com/providenceinnovation/cnp.chatbot-client-web/tree/dev/docs/scrolling.md
    const messagesRefs = useRef<Array<HTMLDivElement | null>>([]);
    const liveChatClosedRef = useRef<HTMLDivElement | null>(null);
    const [isLiveChatClosedInTab, setIsLiveChatClosedInTab] = useState(false);
    const [feedbackStatus, setFeedbackStatus] =
        useState<FeedbackSelectedStatus>();
    const [feedbackToastTimeout, setFeedbackToastTimeout] =
        useState<NodeJS.Timeout>();
    const { inputMode } = useInputMode();
    const { setDisableTextSend } = useDisableTextSend();
    const { isUserWaiting, setIsUserWaiting } = useConversationStatus();

    const isPostBack: (msg: Activity) => boolean = (msg: Activity) => {
        return !!msg.channelData && !!msg.channelData.postBack;
    };

    const isDropdown: (msg: Activity) => boolean = (msg: Activity) => {
        return msg.value && msg.value.title;
    };

    // Determine what message index to "scrollIntoView" (see scrolling doc link above)
    const lastMessage = messages[messages.length - 1];
    const determineScrollMessageIndex = (msgs: Activity[]): number => {
        // if last message is from user, we scroll to it
        const lastIndex = msgs.length - 1;
        if (msgs[lastIndex]?.from.role === 'user') return lastIndex;

        // if last message isn't from user, we need to calculate which bot message to scroll to
        let previousMessage = msgs[0];
        if (!previousMessage) return 0;
        let indexOfFirstBotMessageInLastSequenceOfBotMessages = 0;

        for (let i = 1; i < msgs.length; i++) {
            if (msgs[i].from.role === 'bot') {
                if (
                    previousMessage.from.role === 'user' ||
                    previousMessage.text?.includes('Searching for clinics') // we want to scroll to the carousel message after the "Searching for clinics" message
                ) {
                    indexOfFirstBotMessageInLastSequenceOfBotMessages = i;
                }
            }
            previousMessage = msgs[i];
        }

        return indexOfFirstBotMessageInLastSequenceOfBotMessages;
    };

    useEffect(() => {
        clearTimeout(feedbackToastTimeout);
        if (feedbackStatus) {
            setFeedbackToastTimeout(
                setTimeout(() => {
                    setFeedbackStatus(undefined);
                    clearTimeout(feedbackToastTimeout);
                }, FEEDBACK_TOAST_TIMEOUT_MS)
            );
        }
        return () => {
            clearTimeout(feedbackToastTimeout);
        };
    }, [feedbackStatus]);

    useEffect(() => {
        const lastMsg = messages[messages.length - 1];
        // See scrolling doc (link above) to understand this if statement
        // Make sure the user is able to see the InformationalAlert; do not autoscroll if the user is yet to interact with the page
        if (!interactedWithBot) return;
        if (
            messages.length &&
            (!isPostBack(lastMsg) ||
                isDropdown(lastMsg) ||
                lastMsg?.value?.displayText)
        ) {
            // user's age dropdown response is the only **postback** message we actually display
            // when we don't display a user message (when message is a postBack), re-calculating scroll index will scroll to bottom if not there already
            // we don't want that to happen for example when user is canceling an appointment
            const idx = determineScrollMessageIndex(messages);
            messagesRefs.current[idx]?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length]); // eslint-disable-line

    useEffect(() => {
        if (!liveChatService) {
            return;
        }

        const listener = liveChatService.eventsStream.listen((event) => {
            if (event instanceof OnLiveChatClosedInTabEvent) {
                setIsLiveChatClosedInTab(true);
            }
        });

        return () => {
            listener.dispose();
        };
    }, [liveChatService]);

    // Track whether the user is waiting based on the latest message/activity.
    useEffect(() => {
        const waiting =
            (lastMessage &&
                !lastMessage.text?.startsWith(
                    'requestSkill::cancelAppointment'
                ) &&
                !lastMessage.text?.startsWith(
                    '/navigation.resume-conversation'
                ) &&
                lastMessage.type !== 'event' &&
                (lastMessage.from.role === 'user' ||
                    lastMessage.text?.includes('Searching for clinics'))) ||
            isUserWaitingEvent(lastMessage);
        setIsUserWaiting(!!waiting);
    }, [lastMessage]);

    // Disable text input when user is waiting on bot response
    useEffect(() => {
        setDisableTextSend(isUserWaiting, DisableReasons.USER_WAITING);
    }, [isUserWaiting]);

    removeOldLiveChatAttachments(messages);

    if (messages.length === 0) {
        return <Spinner />;
    }

    const activityElements = [];
    let liveChatIdx = -1;
    for (let idx = 0; idx < messages.length; idx++) {
        const activity = messages[idx];
        const prevActivity = messages.length > 1 ? messages[idx - 1] : null;
        // The greeting intents are done through replayUtterance, which mimics a user re-sending the message. Exclude them for cosmetic reasons
        const prevActivityIsUser =
            prevActivity?.from?.role === 'user' &&
            prevActivity?.text &&
            !['/EXTERNAL_greet_step2', '/EXTERNAL_skip_greet'].includes(
                prevActivity?.text
            );
        const displayBotName =
            activity.name === 'outage_message' ||
            prevActivityIsUser ||
            idx === 0;
        let lastMessageFrom = '';
        let displayLiveChatMessage = true;
        if (isLiveChatEvent(activity)) {
            liveChatIdx++;
            if (prevActivity && isLiveChatEvent(prevActivity)) {
                lastMessageFrom = prevActivity.value.from;
                if (activity.value.id === prevActivity.value.id) {
                    displayLiveChatMessage = false;
                }
            }
        }
        const setCurrentMessageRef = (
            el: HTMLDivElement | null,
            idx: number
        ) => {
            if (el) {
                messagesRefs.current[idx] = el;
            }
        };

        // This logic determines the UI element to render for each activity
        // TODO - could we use enums & a switch case here for better readability?
        const messageElements = isUserMessage(activity) ? (
            <div
                key={`userMessage-${idx}`}
                ref={(el) => setCurrentMessageRef(el, idx)}
            >
                <UserMessage activity={activity} idx={idx} />
            </div>
        ) : isBotMessage(activity) ? (
            <div
                key={`botMessage-${idx}`} // SPECIAL NOTE: it is imperative here that the the key is unique, but won't change between renders. Before I was using uuid().toString() as the value, but that meant that every render of ReactTranscript would generate completely new <BotMessage>'s, so their individual states were being overwritten by new completely new components
                ref={(el) => setCurrentMessageRef(el, idx)}
            >
                <BotMessage
                    activity={activity}
                    disableButtons={
                        idx !== messages.length - 1 &&
                        (idx !== messages.length - 2 ||
                            lastMessage.name !== 'feedbackEvent') &&
                        !lastMessage.text?.startsWith(
                            'requestSkill::cancelAppointment'
                        )
                    }
                    displayBotName={displayBotName}
                    featureFlags={featureFlags}
                    idx={idx}
                    appendMessage={appendMessage}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                    sendEvent={sendEvent}
                    setInputMode={setInputMode}
                    setModalProperties={setModalProperties}
                    setFeedbackStatus={setFeedbackStatus}
                    messageRenderer={messageRenderer}
                />
            </div>
        ) : isLiveChatUserEvent(activity) && displayLiveChatMessage ? (
            <div
                key={`userMessage-${idx}`}
                ref={(el) => setCurrentMessageRef(el, idx)}
            >
                <LiveChatUserMessage activity={activity} idx={liveChatIdx} />
            </div>
        ) : isLiveChatAgentEvent(activity) && displayLiveChatMessage ? (
            <div
                key={`agentMessage-${idx}`}
                ref={(el) => setCurrentMessageRef(el, idx)}
            >
                <LiveChatAgentMessage
                    activity={activity}
                    idx={liveChatIdx}
                    lastMessageFrom={lastMessageFrom}
                />
            </div>
        ) : isUserWaitingEvent(activity) ? (
            <div
                key={`userWaiting-${idx}`}
                ref={(el) => setCurrentMessageRef(el, idx)}
            >
                <BotTakingAwhile
                    disableButtons={false}
                    displayBotName={displayBotName}
                    featureFlags={featureFlags}
                    idx={idx}
                    appendMessage={appendMessage}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                    sendEvent={sendEvent}
                    setInputMode={setInputMode}
                    setModalProperties={setModalProperties}
                    setFeedbackStatus={setFeedbackStatus}
                    messageRenderer={messageRenderer}
                />
            </div>
        ) : null;

        activityElements.push(messageElements);
    }

    const anchoredElementsActivity = getActivityWithAnchoredElements(messages);
    const anchoredElements = anchoredElementsActivity ? (
        <AnchoredElementContainer
            activity={anchoredElementsActivity}
            featureFlags={featureFlags}
            sendMessage={sendMessage}
            sendPostBack={sendPostBack}
        />
    ) : null;

    const scrollbarClass =
        isMobile() || isMobileBrowser() || isUnified()
            ? null
            : styles.transcriptScrollBar;

    let liveChatClosedInTab = null;
    if (isLiveChatClosedInTab) {
        liveChatClosedInTab = (
            <div ref={liveChatClosedRef}>
                <LiveChatClosedInTab featureFlags={featureFlags} />
            </div>
        );
        liveChatClosedRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const paddingClass =
        inputMode === 'enabled' ? null : styles.addPaddingBottom;

    return (
        <div
            className={classNames(
                styles.transcriptContainer,
                paddingClass,
                scrollbarClass
            )}
        >
            {/* Emergency Banner */}
            <InformationalAlert
                content={generalConfig?.providence?.emergencyBannerContent}
            />
            <div className={styles.transcriptContainerMessages}>
                {activityElements}
                {liveChatClosedInTab}
                {/* render typing ellipsis, track long latency delays, and display bot timeout message */}
                {/* Longer-than-usual is emitted as a userWaiting event and rendered within the transcript */}
                <UserWaitingOnBot
                    botMessageDeps={{
                        featureFlags,
                        sendMessage,
                        sendPostBack,
                        sendEvent,
                        setInputMode,
                        messageRenderer,
                    }}
                />
            </div>
            {anchoredElements}
            <div
                className={classNames(styles.shadow, styles[getTenantName()])}
            ></div>
            {feedbackStatus && (
                <GenericToast
                    text="Thank you for your feedback!"
                    setStatus={setFeedbackStatus}
                />
            )}
        </div>
    );
};
